"use client"

import * as React from "react"
import * as SelectPrimitive from "@radix-ui/react-select"
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  SearchIcon,
} from "lucide-react"

import { cn, compararTexto, normalizarBusca } from "@/lib/utils"

/**
 * Valores de opções "especiais" (Todos, Nenhuma, Não definido...) que ficam
 * sempre no topo da lista, fora da ordenação alfabética.
 */
const VALORES_FIXOS_NO_TOPO = new Set(["*", "-1", "__none__", "nenhuma", ""])

const SELETOR_ITEM_VISIVEL = "[data-slot=select-item]:not([data-disabled])"

type SelectBuscaContextValue = {
  busca: string
  setBusca: React.Dispatch<React.SetStateAction<string>>
  focoInicialFeitoRef: React.RefObject<boolean>
}

const SelectBuscaContext = React.createContext<SelectBuscaContextValue | null>(
  null
)

function Select({
  onOpenChange,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Root>) {
  const [busca, setBusca] = React.useState("")
  const focoInicialFeitoRef = React.useRef(false)
  const contexto = React.useMemo(
    () => ({ busca, setBusca, focoInicialFeitoRef }),
    [busca]
  )

  const handleOpenChange = (aberto: boolean) => {
    if (aberto) focoInicialFeitoRef.current = false
    else setBusca("")
    onOpenChange?.(aberto)
  }

  return (
    <SelectBuscaContext.Provider value={contexto}>
      <SelectPrimitive.Root
        data-slot="select"
        onOpenChange={handleOpenChange}
        {...props}
      />
    </SelectBuscaContext.Provider>
  )
}

function SelectGroup({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />
}

function SelectValue({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />
}

function SelectTrigger({
  className,
  size = "default",
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
  size?: "sm" | "default"
}) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      className={cn(
        "border-input data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 flex w-fit items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-9 data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDownIcon className="size-4 opacity-50" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  )
}

/** Texto visível de um nó React (usado para ordenar e buscar). */
function extrairTexto(node: React.ReactNode): string {
  if (node == null || typeof node === "boolean") return ""
  if (typeof node === "string" || typeof node === "number") return String(node)
  if (Array.isArray(node)) return node.map(extrairTexto).join("")
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
    return extrairTexto(node.props.children)
  }
  return ""
}

type ElementoComFilhos = React.ReactElement<{ children?: React.ReactNode }>
type ElementoItem = React.ReactElement<
  React.ComponentProps<typeof SelectPrimitive.Item>
>

function ehItem(node: React.ReactNode): node is ElementoItem {
  return React.isValidElement(node) && node.type === SelectItem
}

function ehContainer(node: React.ReactNode): node is ElementoComFilhos {
  return (
    React.isValidElement(node) &&
    (node.type === SelectGroup || node.type === React.Fragment)
  )
}

/** Achata arrays e fragments mantendo chaves únicas. */
function achatarFilhos(children: React.ReactNode): React.ReactNode[] {
  return React.Children.toArray(children).flatMap((filho) => {
    if (!React.isValidElement(filho) || filho.type !== React.Fragment) {
      return [filho]
    }
    const prefixo = String(filho.key)
    return achatarFilhos((filho as ElementoComFilhos).props.children).map(
      (neto) =>
        React.isValidElement(neto)
          ? React.cloneElement(neto, { key: `${prefixo}${neto.key}` })
          : neto
    )
  })
}

function ordenarBloco(itens: ElementoItem[]): ElementoItem[] {
  const fixos = itens.filter((i) => VALORES_FIXOS_NO_TOPO.has(i.props.value))
  const demais = itens
    .filter((i) => !VALORES_FIXOS_NO_TOPO.has(i.props.value))
    .sort((a, b) =>
      compararTexto(
        extrairTexto(a.props.children),
        extrairTexto(b.props.children)
      )
    )
  return [...fixos, ...demais]
}

/**
 * Ordena os itens (números crescentes, textos em ordem alfabética).
 * Cada sequência de itens entre labels/separadores é ordenada separadamente.
 */
function ordenarItens(children: React.ReactNode): React.ReactNode[] {
  const resultado: React.ReactNode[] = []
  let bloco: ElementoItem[] = []

  for (const filho of achatarFilhos(children)) {
    if (ehItem(filho)) {
      bloco.push(filho)
      continue
    }
    resultado.push(...ordenarBloco(bloco))
    bloco = []
    resultado.push(
      ehContainer(filho)
        ? React.cloneElement(
            filho,
            undefined,
            ordenarItens(filho.props.children)
          )
        : filho
    )
  }
  resultado.push(...ordenarBloco(bloco))
  return resultado
}

function contarItens(children: React.ReactNode, busca: string): number {
  return achatarFilhos(children).reduce<number>((total, filho) => {
    if (ehItem(filho)) {
      const texto = normalizarBusca(extrairTexto(filho.props.children))
      return texto.includes(busca) ? total + 1 : total
    }
    if (ehContainer(filho)) {
      return total + contarItens(filho.props.children, busca)
    }
    return total
  }, 0)
}

const ehTelaDeToque = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(pointer: coarse)").matches

function SelectBusca({
  placeholder,
  inputRef,
}: {
  placeholder: string
  inputRef: React.RefObject<HTMLInputElement | null>
}) {
  const contexto = React.useContext(SelectBuscaContext)
  if (!contexto) return null
  const { busca, setBusca } = contexto

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Setas, Tab e Esc seguem para o Radix (navegação e fechamento).
    if (["ArrowDown", "ArrowUp", "Tab", "Escape"].includes(event.key)) return
    event.stopPropagation()
    if (event.key !== "Enter") return

    // Enter seleciona o primeiro resultado da busca.
    event.preventDefault()
    const primeiro = event.currentTarget
      .closest("[data-slot=select-content]")
      ?.querySelector<HTMLElement>(SELETOR_ITEM_VISIVEL)
    if (!primeiro) return
    primeiro.focus()
    primeiro.dispatchEvent(
      new KeyboardEvent("keydown", { key: "Enter", bubbles: true })
    )
  }

  return (
    <div
      data-slot="select-search"
      className="flex items-center gap-2 border-b px-2"
    >
      <SearchIcon className="text-muted-foreground size-4 shrink-0" />
      <input
        ref={inputRef}
        value={busca}
        onChange={(event) => setBusca(event.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        aria-label={placeholder}
        autoComplete="off"
        className="placeholder:text-muted-foreground h-9 w-full bg-transparent text-sm outline-none"
      />
    </div>
  )
}

function SelectContent({
  className,
  children,
  position = "popper",
  searchable = true,
  sortable = true,
  searchPlaceholder = "Buscar...",
  onKeyDownCapture,
  onFocusCapture,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content> & {
  /** Exibe o campo de busca no topo da lista (padrão: true). */
  searchable?: boolean
  /** Ordena as opções em ordem crescente/alfabética (padrão: true). */
  sortable?: boolean
  searchPlaceholder?: string
}) {
  const contexto = React.useContext(SelectBuscaContext)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const comBusca = searchable && contexto !== null
  const busca = comBusca ? normalizarBusca(contexto.busca) : ""
  const itens = sortable ? ordenarItens(children) : children
  const semResultados = busca !== "" && contarItens(children, busca) === 0

  // Ao abrir, o Radix foca o item selecionado; levamos o foco para a busca.
  const handleFocusCapture = (event: React.FocusEvent<HTMLDivElement>) => {
    onFocusCapture?.(event)
    if (!comBusca || contexto.focoInicialFeitoRef.current) return
    contexto.focoInicialFeitoRef.current = true
    if (event.target === inputRef.current || ehTelaDeToque()) return
    inputRef.current?.focus({ preventScroll: true })
  }

  // Digitar com o foco em um item continua escrevendo na busca.
  const handleKeyDownCapture = (event: React.KeyboardEvent<HTMLDivElement>) => {
    onKeyDownCapture?.(event)
    const input = inputRef.current
    if (!comBusca || !input || event.target === input) return

    const { key, ctrlKey, metaKey, altKey } = event
    const primeiroItem = event.currentTarget.querySelector(SELETOR_ITEM_VISIVEL)
    const voltarParaBusca = key === "ArrowUp" && event.target === primeiroItem
    const digitando =
      key.length === 1 &&
      !ctrlKey &&
      !metaKey &&
      !altKey &&
      !(key === " " && contexto.busca === "")
    const apagando = key === "Backspace" && contexto.busca !== ""
    if (!voltarParaBusca && !digitando && !apagando) return

    event.preventDefault()
    event.stopPropagation()
    if (digitando) contexto.setBusca((atual) => atual + key)
    if (apagando) contexto.setBusca((atual) => atual.slice(0, -1))
    input.focus({ preventScroll: true })
  }

  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        className={cn(
          "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-(--radix-select-content-available-height) min-w-[8rem] origin-(--radix-select-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md border shadow-md",
          position === "popper" &&
            "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          className
        )}
        position={position}
        onKeyDownCapture={handleKeyDownCapture}
        onFocusCapture={handleFocusCapture}
        {...props}
      >
        {comBusca && (
          <SelectBusca placeholder={searchPlaceholder} inputRef={inputRef} />
        )}
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            "p-1",
            position === "popper" &&
              "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1"
          )}
        >
          {itens}
          {semResultados && (
            <div className="text-muted-foreground px-2 py-1.5 text-center text-sm">
              Nenhum resultado encontrado
            </div>
          )}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
}

function SelectLabel({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn("text-muted-foreground px-2 py-1.5 text-xs", className)}
      {...props}
    />
  )
}

function SelectItem({
  className,
  children,
  disabled,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) {
  const busca = normalizarBusca(
    React.useContext(SelectBuscaContext)?.busca ?? ""
  )
  // Itens filtrados continuam montados (só escondidos) para o SelectValue
  // seguir exibindo o texto do item selecionado.
  const oculto =
    busca !== "" && !normalizarBusca(extrairTexto(children)).includes(busca)

  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      disabled={disabled || oculto}
      className={cn(
        "focus:bg-accent focus:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        oculto && "hidden",
        className
      )}
      {...props}
    >
      <span className="absolute right-2 flex size-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  )
}

function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn("bg-border pointer-events-none -mx-1 my-1 h-px", className)}
      {...props}
    />
  )
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className
      )}
      {...props}
    >
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  )
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className
      )}
      {...props}
    >
      <ChevronDownIcon className="size-4" />
    </SelectPrimitive.ScrollDownButton>
  )
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
}
